"use client"

import img1 from '@/components/gfx/stellplatz-bilder/0zingst-wohnmobil-stellplatz.jpg'
import img2 from '@/components/gfx/stellplatz-bilder/bester-wohnmobil-stellplatz.jpg'
import img3 from '@/components/gfx/stellplatz-bilder/einfahrt-ostsee-stellplatz-zingst.jpg'
import img4 from '@/components/gfx/stellplatz-bilder/lalasurfbar-zingst.jpg'
import img5 from '@/components/gfx/stellplatz-bilder/ostseeparkplatz-zingst.jpg'
import img6 from '@/components/gfx/stellplatz-bilder/ostseestrand-urlaub-zingst.jpg'
import img7 from '@/components/gfx/stellplatz-bilder/parkplatz-ostsee-zingst.jpg'
import img8 from '@/components/gfx/stellplatz-bilder/sunset-ostsee-zingst.jpg'
import img9 from '@/components/gfx/stellplatz-bilder/wohnmobil-stellplatz-ostsee-zingst.jpg'
import img10 from '@/components/gfx/stellplatz-bilder/wohnmobil-stellplatz-ostseestrand.jpg'
import img11 from '@/components/gfx/stellplatz-bilder/wohnmobilstellplatz-zingst-ostsee.jpg'
import img12 from '@/components/gfx/stellplatz-bilder/wohnmobilstellplatz-zingst.jpg'
import img13 from '@/components/gfx/stellplatz-bilder/zingst-ostsee-parkplatz.jpg'
import img14 from '@/components/gfx/stellplatz-bilder/zingst-urlaub.jpg'
import img15 from '@/components/gfx/stellplatz-bilder/zingst-zimmervermittlung-stellplatz.jpg'
import { captureClientEvent } from "@/lib/telemetry-client";
import { cn } from "@/utils/utils"
import Image from "next/image"
import Link from "next/link"
import { useCallback, useEffect, useState } from "react"
import { useInView } from "react-intersection-observer"
import { motion, AnimatePresence } from "framer-motion"
import { FaStar, FaArrowDown } from 'react-icons/fa6'

const images = [
    { url: img1, alt: 'Zingst Wohnmobil Stellplatz - Campingplatz in Zingst' },
    { url: img2, alt: 'Bester Wohnmobil Stellplatz - Top Camping in Zingst' },
    { url: img3, alt: 'Einfahrt zum Ostsee Stellplatz Zingst - Einladender Parkbereich' },
    { url: img4, alt: 'Lalasurfbar Zingst - Beliebte Bar am Strand' },
    { url: img5, alt: 'Ostseeparkplatz Zingst - Idealer Standort für Wohnmobile' },
    { url: img6, alt: 'Ostseestrand Urlaub Zingst - Traumhafter Strandurlaub' },
    { url: img7, alt: 'Parkplatz Ostsee Zingst - Bequemer Zugang zum Strand' },
    { url: img8, alt: 'Sunset am Ostsee Zingst - Atemberaubende Sonnenuntergänge' },
    { url: img9, alt: 'Wohnmobil Stellplatz Ostsee Zingst - Perfekter Campingplatz' },
    { url: img10, alt: 'Wohnmobil Stellplatz Ostseestrand - Direkter Zugang zum Strand' },
    { url: img11, alt: 'Wohnmobilstellplatz Zingst Ostsee - Beliebte Lage für Reisemobile' },
    { url: img12, alt: 'Wohnmobilstellplatz Zingst - Ruhiger Campingplatz in der Natur' },
    { url: img13, alt: 'Zingst Ostsee Parkplatz - Bequem für Tagesausflüge' },
    { url: img14, alt: 'Zingst Urlaub - Unvergessliche Erholung an der Ostsee' },
    { url: img15, alt: 'Zingst Zimmervermittlung Stellplatz - Unterkünfte in Zingst' },
];

export function Hero({ className }: { className?: string }) {
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);

    const changeImage = useCallback((next: boolean) => {
        setActiveImageIndex((prevIndex) => {
            const nextIndex = next
                ? (prevIndex + 1) % images.length
                : (prevIndex - 1 + images.length) % images.length;
            captureClientEvent("hero_image_changed", {
                from_index: prevIndex,
                to_index: nextIndex,
                direction: next ? "next" : "previous",
            });
            return nextIndex;
        });
    }, []);

    useEffect(() => {
        setIsLoaded(true);

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowRight') {
                changeImage(true);
            } else if (event.key === 'ArrowLeft') {
                changeImage(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [changeImage]);

    const { ref: heroRef, inView } = useInView({
        threshold: 0.2,
    });

    useEffect(() => {
        if (!inView) return;

        const interval = setInterval(() => {
            setActiveImageIndex((prev) => (prev + 1) % images.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [inView]);

    return (
        <div
            ref={heroRef}
            className={cn(
                "relative min-h-screen w-full overflow-hidden",
                className
            )}
        >
            {/* Background Images with Ken Burns effect */}
            <div className="absolute inset-0">
                {images.map((image, index) => (
                    <motion.div
                        key={index}
                        initial={false}
                        animate={{
                            opacity: index === activeImageIndex ? 1 : 0,
                            scale: index === activeImageIndex ? 1.05 : 1,
                        }}
                        transition={{ duration: 1.5, ease: "easeInOut" }}
                        className="absolute inset-0"
                    >
                        <Image
                            src={image.url}
                            alt={image.alt}
                            placeholder='blur'
                            quality={85}
                            priority={index === 0}
                            loading={index < 3 ? "eager" : "lazy"}
                            className="h-full w-full object-cover"
                            fill
                        />
                    </motion.div>
                ))}

                {/* Gradient overlays for depth */}
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-transparent to-slate-900/60" />
                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-transparent to-amber-500/10" />
            </div>

            {/* Main Content */}
            <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4">
                {/* Hero Title */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: isLoaded ? 1 : 0, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-center"
                >
                    {/* Handwritten accent */}
                    <motion.span
                        initial={{ opacity: 0, rotate: -5 }}
                        animate={{ opacity: isLoaded ? 1 : 0, rotate: 0 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="mb-2 inline-block font-handwritten text-2xl text-amber-300 md:text-3xl"
                    >
                        Willkommen beim
                    </motion.span>

                    <h1 className="relative">
                        <span className="block font-special text-5xl font-bold tracking-tight text-white drop-shadow-lg sm:text-6xl md:text-7xl lg:text-8xl">
                            Stellplatz
                        </span>
                        <span className="mt-1 block font-special text-4xl font-bold text-amber-400 drop-shadow-lg sm:text-5xl md:text-6xl lg:text-7xl">
                            Zingst Ostsee
                        </span>
                    </h1>

                    {/* Trust badges - moved higher for visibility */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: isLoaded ? 1 : 0, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        className="mt-6 flex flex-col items-center gap-3"
                    >
                        {/* Star rating - prominent position */}
                        <div className="flex items-center gap-1 rounded-full bg-slate-900/60 px-4 py-2 backdrop-blur-sm">
                            <FaStar className="h-4 w-4 text-amber-400" />
                            <FaStar className="h-4 w-4 text-amber-400" />
                            <FaStar className="h-4 w-4 text-amber-400" />
                            <FaStar className="h-4 w-4 text-amber-400" />
                            <FaStar className="h-4 w-4 text-amber-400" />
                            <span className="ml-2 text-sm font-medium text-white">200+ Google Reviews</span>
                        </div>

                        <p className="max-w-lg text-lg text-white/90 md:text-xl">
                            PKW & Wohnmobil direkt hinterm Deich
                        </p>

                        {/* No reservation badge - key differentiator */}
                        <div className="flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/20 px-4 py-2 backdrop-blur-sm">
                            <span className="text-sm font-medium text-emerald-300">Keine Reservierung nötig - einfach ankommen!</span>
                        </div>
                    </motion.div>
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: isLoaded ? 1 : 0, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
                >
                    <Link href="#preise">
                        <button
                            onClick={() => captureClientEvent("hero_cta_clicked", { cta: "ticket_kaufen" })}
                            className="group relative overflow-hidden rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-8 py-4 font-semibold text-slate-900 shadow-lg shadow-amber-500/30 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/40"
                        >
                            <span className="relative z-10 flex items-center gap-2">
                                Jetzt Ticket kaufen
                                <FaArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-1" />
                            </span>
                            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-white/0 via-white/30 to-white/0 transition-transform duration-500 group-hover:translate-x-full" />
                        </button>
                    </Link>
                    <Link href="#anfahrt">
                        <button
                            onClick={() => captureClientEvent("hero_cta_clicked", { cta: "anfahrt_infos" })}
                            className="rounded-full border-2 border-white/30 bg-white/10 px-8 py-4 font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:border-white/50 hover:bg-white/20"
                        >
                            Anfahrt & Infos
                        </button>
                    </Link>
                </motion.div>

            </div>

            {/* Image indicators */}
            <div className="absolute bottom-18 left-1/2 z-20 flex -translate-x-1/2 gap-2">
                {images.slice(0, 6).map((_, index) => (
                    <button
                        key={index}
                        onClick={() => {
                            captureClientEvent("hero_image_dot_clicked", {
                                from_index: activeImageIndex,
                                to_index: index,
                            });
                            setActiveImageIndex(index);
                        }}
                        className={cn(
                            "h-2 rounded-full transition-all duration-300",
                            index === activeImageIndex % 6
                                ? "w-8 bg-amber-400"
                                : "w-2 bg-white/40 hover:bg-white/60"
                        )}
                        aria-label={`Bild ${index + 1} anzeigen`}
                    />
                ))}
            </div>

        </div>
    )
}
